import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import { supabase } from './lib/supabase';
import AdminUsers from './AdminUsers';
import AdminAddService from './AdminAddService';
import AdminServicesList from './AdminServicesList';
import AdminBookings from './AdminBookings';
import AdminAvailability from './AdminAvailability';
import AdminAddSpecialist from './AdminAddSpecialist';
import AdminSpecialistsList from './AdminSpecialistsList';
import AdminAddTrainer from './AdminAddTrainer';
import AdminTrainersList from './AdminTrainersList';
import AdminChat from './AdminChat';
import './AdminDashboard.css';

function AdminDashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if admin is logged in
    const adminLoggedIn = localStorage.getItem('adminLoggedIn');
    if (!adminLoggedIn) {
      navigate('/admin-login');
    }

    // Handle window resize
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [navigate]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊', path: '/admin-dashboard' },
    { id: 'bookings', label: 'Bookings', icon: '📅', path: '/admin-dashboard/bookings' },
    { id: 'availability', label: 'Availability', icon: '⏰', path: '/admin-dashboard/availability' },
    { id: 'services-add', label: 'Add New Service', icon: '✨', path: '/admin-dashboard/services/add' },
    { id: 'services-list', label: 'All Services', icon: '📋', path: '/admin-dashboard/services' },
    { id: 'specialists-add', label: 'Add Specialist', icon: '👨‍⚕️', path: '/admin-dashboard/specialists/add' },
    { id: 'specialists-list', label: 'Specialists', icon: '👨‍⚕️', path: '/admin-dashboard/specialists' },
    { id: 'trainers-add', label: 'Add Training Session', icon: '📚', path: '/admin-dashboard/trainers/add' },
    { id: 'trainers-list', label: 'Training Sessions', icon: '📚', path: '/admin-dashboard/trainers' },
    { id: 'users', label: 'Users', icon: '👥', path: '/admin-dashboard/users' },
    { id: 'chat', label: 'Chat', icon: '💬', path: '/admin-dashboard/chat' },
  ];

  const getActivePage = () => {
    const path = location.pathname;
    if (path === '/admin-dashboard' || path === '/admin-dashboard/') return 'dashboard';
    if (path.startsWith('/admin-dashboard/services/add')) return 'services-add';
    if (path.startsWith('/admin-dashboard/services')) return 'services-list';
    if (path.startsWith('/admin-dashboard/specialists/add')) return 'specialists-add';
    if (path.startsWith('/admin-dashboard/specialists')) return 'specialists-list';
    if (path.startsWith('/admin-dashboard/trainers/add')) return 'trainers-add';
    if (path.startsWith('/admin-dashboard/trainers')) return 'trainers-list';
    if (path.startsWith('/admin-dashboard/users')) return 'users';
    if (path.startsWith('/admin-dashboard/bookings')) return 'bookings';
    if (path.startsWith('/admin-dashboard/availability')) return 'availability';
    if (path.startsWith('/admin-dashboard/chat')) return 'chat';
    return 'dashboard';
  };

  const activePage = getActivePage();

  return (
    <div className="admin-dashboard">
      {/* Mobile Header */}
      {isMobile && (
        <div className="admin-mobile-header">
          <div className="mobile-header-left">
            <span className="admin-panel-text">Admin Panel</span>
          </div>
          <button 
            className="mobile-toggle-btn"
            onClick={toggleSidebar}
            aria-label="Toggle sidebar"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {sidebarOpen ? (
                <path d="M18 6L6 18M6 6l12 12" />
              ) : (
                <path d="M3 12h18M3 6h18M3 18h18" />
              )}
            </svg>
          </button>
        </div>
      )}

      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2 className="sidebar-title">Admin Panel</h2>
        </div>
        <nav className="sidebar-nav">
          <ul className="sidebar-menu">
            {sidebarItems.map((item) => (
              <li key={item.id} className="sidebar-menu-item">
                <button 
                  className={`sidebar-menu-link ${activePage === item.id ? 'active' : ''}`}
                  onClick={() => {
                    navigate(item.path);
                    closeSidebar();
                  }}
                >
                  <span className="menu-icon">{item.icon}</span>
                  <span className="menu-label">{item.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>
        <div className="sidebar-footer">
          <button 
            className="logout-btn"
            onClick={() => {
              localStorage.removeItem('adminLoggedIn');
              localStorage.removeItem('adminEmail');
              navigate('/admin-login');
            }}
          >
            <span className="menu-icon">🚪</span>
            <span className="menu-label">Logout</span>
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {isMobile && sidebarOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar}></div>
      )}

      {/* Main Content Area */}
      <main className="admin-main-content">
        <div className="content-wrapper">
          <Routes>
            <Route path="/" element={<DashboardStats />} />
            <Route path="/users" element={<AdminUsers />} />
            <Route path="/services/add" element={<AdminAddService />} />
            <Route path="/services/edit/:id" element={<AdminAddService />} />
            <Route path="/services" element={<AdminServicesList />} />
            <Route path="/specialists/add" element={<AdminAddSpecialist />} />
            <Route path="/specialists/edit/:id" element={<AdminAddSpecialist />} />
            <Route path="/specialists" element={<AdminSpecialistsList />} />
            <Route path="/trainers/add" element={<AdminAddTrainer />} />
            <Route path="/trainers/edit/:id" element={<AdminAddTrainer />} />
            <Route path="/trainers" element={<AdminTrainersList />} />
            <Route path="/bookings" element={<AdminBookings />} />
            <Route path="/availability" element={<AdminAvailability />} />
            <Route path="/chat" element={<AdminChat />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

// Dashboard Statistics Component
function DashboardStats() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalServices: 0,
    totalBookings: 0,
    pendingBookings: 0,
    confirmedBookings: 0,
    completedBookings: 0,
    cancelledBookings: 0,
    healingSessions: 0,
    trainingSessions: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Fetch all stats in parallel
      const [
        usersResult,
        servicesResult,
        bookingsResult,
        healingResult,
        trainingResult
      ] = await Promise.all([
        supabase.from('users').select('*', { count: 'exact', head: true }),
        supabase.from('services').select('*', { count: 'exact', head: true }),
        supabase.from('bookings').select('id, status'),
        supabase.from('services').select('*', { count: 'exact', head: true }).eq('category', 'Healing Session'),
        supabase.from('services').select('*', { count: 'exact', head: true }).eq('category', 'Training Session')
      ]);

      // Count bookings by status
      const bookings = bookingsResult.data || [];
      const pendingBookings = bookings.filter(b => b.status === 'pending').length;
      const confirmedBookings = bookings.filter(b => b.status === 'confirmed').length;
      const completedBookings = bookings.filter(b => b.status === 'completed').length;
      const cancelledBookings = bookings.filter(b => b.status === 'cancelled').length;

      setStats({
        totalUsers: usersResult.count || 0,
        totalServices: servicesResult.count || 0,
        totalBookings: bookings.length,
        pendingBookings,
        confirmedBookings,
        completedBookings,
        cancelledBookings,
        healingSessions: healingResult.count || 0,
        trainingSessions: trainingResult.count || 0
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('Failed to load statistics. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="dashboard-container">
        <h1 className="content-title">Dashboard</h1>
        <div className="stats-loading">
          <p>Loading statistics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <h1 className="content-title">Dashboard</h1>
        <div className="stats-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <h1 className="content-title">Dashboard</h1>
      <p className="content-subtitle">Overview of your platform statistics</p>

      <div className="stats-grid">
        <div className="stat-card stat-card-primary">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3 className="stat-label">Total Users</h3>
            <p className="stat-value">{stats.totalUsers}</p>
          </div>
        </div>

        <div className="stat-card stat-card-primary">
          <div className="stat-icon">✨</div>
          <div className="stat-content">
            <h3 className="stat-label">Total Services</h3>
            <p className="stat-value">{stats.totalServices}</p>
          </div>
        </div>

        <div className="stat-card stat-card-primary">
          <div className="stat-icon">📅</div>
          <div className="stat-content">
            <h3 className="stat-label">Total Bookings</h3>
            <p className="stat-value">{stats.totalBookings}</p>
          </div>
        </div>

        <div className="stat-card stat-card-warning">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <h3 className="stat-label">Pending Bookings</h3>
            <p className="stat-value">{stats.pendingBookings}</p>
          </div>
        </div>

        <div className="stat-card stat-card-success">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3 className="stat-label">Confirmed Bookings</h3>
            <p className="stat-value">{stats.confirmedBookings}</p>
          </div>
        </div>

        <div className="stat-card stat-card-info">
          <div className="stat-icon">✓</div>
          <div className="stat-content">
            <h3 className="stat-label">Completed Bookings</h3>
            <p className="stat-value">{stats.completedBookings}</p>
          </div>
        </div>

        <div className="stat-card stat-card-secondary">
          <div className="stat-icon">🕯️</div>
          <div className="stat-content">
            <h3 className="stat-label">Healing Sessions</h3>
            <p className="stat-value">{stats.healingSessions}</p>
          </div>
        </div>

        <div className="stat-card stat-card-secondary">
          <div className="stat-icon">📚</div>
          <div className="stat-content">
            <h3 className="stat-label">Training Sessions</h3>
            <p className="stat-value">{stats.trainingSessions}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;

